import { Component, ElementRef, inject, Input, OnInit, TemplateRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbstractValueAccessorComponent } from './abstract-value-accessor.component';
import dayjs from 'dayjs/esm';
import localData from 'dayjs/esm/plugin/localeData';
import 'dayjs/esm/locale/de';
import 'dayjs/esm/locale/en-gb';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';

dayjs.extend(localData);

@UntilDestroy()
@Component({
  template: '',
  standalone: false,
})
export abstract class AbstractDateComponent<T> extends AbstractValueAccessorComponent<T> implements OnInit {
  private translocoService = inject(TranslocoService);
  private configPrime = inject(PrimeNG);

  _defaultDate = dayjs().hour(0).minute(0).second(0).toDate();
  minDateFormatted: Date;
  defaultMinDate: Date;
  @Input() disabledDates: Date[];

  @Input() appendTo: HTMLElement | ElementRef | TemplateRef<unknown> | string | null | undefined;

  @Input() set minDate(value: dayjs.Dayjs) {
    if (value) {
      const minDateValue = dayjs(value);
      this.defaultMinDate = minDateValue.toDate();
      this.minDateFormatted = minDateValue.toDate();
    } else {
      this.minDateFormatted = null;
      this.defaultMinDate = null;
    }
  }

  maxDateFormatted: Date;
  defaultMaxDate: Date;

  @Input() set maxDate(value: dayjs.Dayjs) {
    if (value) {
      const maxDateValue = dayjs(value);
      this.defaultMaxDate = maxDateValue.toDate();
      this.maxDateFormatted = maxDateValue.toDate();
    }
  }

  get defaultDate(): Date {
    return this.defaultMinDate || this._defaultDate;
  }

  @Input() set: string;
  @Input() cancel: string;
  localeFormat: string;
  monthRange: string;
  @Input() dateMin: Date;
  @Input() dateMax: Date;

  ngOnInit(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (language: string) => {
        if (language === 'en') {
          language = 'en-gb';
        }
        this.monthRange = this.setLocaleMonthRange(language);
        this.primeNGMonths(language);
        this.localeFormat = this.configPrime.getTranslation('dateFormat');
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  setLocaleMonthRange(locale: string) {
    let format: string;
    if (locale === 'de') {
      format = 'MM.YYYY';
    } else {
      format = 'MM/YYYY';
    }
    return format;
  }

  primeNGMonths(locale = 'en') {
    const dayjsLocale = dayjs().locale(locale).localeData();
    this.configPrime.setTranslation({
      dayNamesMin: dayjsLocale.weekdaysMin(),
      monthNames: dayjsLocale.months(),
      monthNamesShort: dayjsLocale.monthsShort(),
      firstDayOfWeek: dayjsLocale.firstDayOfWeek(),
      dateFormat: dayjsLocale.longDateFormat('L'),
      today: this.set,
      clear: this.cancel,
    });
  }
}
